import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Zap, Clock, Check } from "lucide-react";
import { SCHENGEN_COUNTRIES, codeFor } from "@/lib/countries";
import { WORLD_COUNTRIES, SCHENGEN_CODES } from "@/lib/worldCountries";
import { PreviewCard } from "@/components/DocumentMockups";
import ScrollRow from "@/components/ScrollRow";
import EligibilityBanner from "@/components/EligibilityBanner";
import { CountryCombobox } from "@/components/CountryCombobox";
import api from "@/lib/api";
import { ADD_ONS, addOnPrice, labelForItems } from "@/lib/addons";
import { VISA_FREE_CODES } from "@/lib/worldCountries";

export default function HeroApplicationCard({ defaultPackages = [], horizontalFields = false }) {
  const navigate = useNavigate();
  const [nationality, setNationality] = useState("");        // ISO-2
  const [applyingFrom, setApplyingFrom] = useState("");      // ISO-2
  const [country, setCountry] = useState("");                // Schengen name
  const [delivery, setDelivery] = useState("normal");
  const [selectedPkg, setSelectedPkg] = useState("complete");
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [packages, setPackages] = useState(defaultPackages);
  const [loading, setLoading] = useState(false);
  const [visaFreeAck, setVisaFreeAck] = useState(false);

  useEffect(() => { setPackages(defaultPackages); }, [defaultPackages]);

  // Reset downstream selections (and visa-free ack) when nationality changes
  useEffect(() => { setApplyingFrom(""); setCountry(""); setVisaFreeAck(false); }, [nationality]);
  useEffect(() => { setCountry(""); }, [applyingFrom]);

  // Fetch packages for the chosen destination
  useEffect(() => {
    if (!country) return;
    const code = codeFor(country);
    if (!code) return;
    setLoading(true);
    api.get(`/packages/${code}`)
      .then((r) => {
        setPackages(r.data || []);
        if (!(r.data || []).some((p) => p.id === selectedPkg)) {
          setSelectedPkg(r.data?.[0]?.id || "");
        }
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country]);

  const priceFor = (p) => (delivery === "express" ? p.price_express : p.price_normal);
  const addOnTotal = addOnPrice(selectedAddons);
  const toggleAddon = (id) => {
    setSelectedAddons((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };
  const isSchengenNational = nationality && SCHENGEN_CODES.has(nationality);
  const isVisaFreeUnacked = nationality && VISA_FREE_CODES.has(nationality) && !visaFreeAck;
  const blocked = isSchengenNational || isVisaFreeUnacked;
  const canStart = !!country && !!selectedPkg && !blocked;

  const start = () => {
    const params = new URLSearchParams({ package: selectedPkg, delivery });
    if (nationality) params.set("nationality", nationality);
    if (applyingFrom) params.set("applying_from", applyingFrom);
    if (country) params.set("country", country);
    if (selectedAddons.length > 0) params.set("addons", selectedAddons.join(","));
    navigate(`/apply?${params.toString()}`);
  };

  return (
    <div className={`${horizontalFields ? "w-full max-w-3xl" : "w-full lg:w-[420px]"} bg-white text-[#0f1b2d] rounded-xl shadow-2xl border border-white/10 p-6 reveal`} data-testid="hero-app-card">
      <div>
        <div className="text-xs uppercase tracking-widest text-[#64748b]">Get started</div>
        <h3 className="font-display font-bold text-xl">Start your application</h3>
      </div>

      {/* Country trio — horizontal on md+ when horizontalFields, otherwise stacked */}
      <div className={`mt-5 grid ${horizontalFields ? "grid-cols-1 md:grid-cols-3 gap-3" : "grid-cols-1 gap-4"}`} data-testid="hero-country-trio">
        {/* Step 1: Nationality */}
        <div className="flex flex-col">
          <label className="text-xs font-semibold text-[#0f1b2d]">Your nationality</label>
          <div className="mt-auto pt-1.5">
            <CountryCombobox
              options={WORLD_COUNTRIES}
              value={nationality}
              onChange={setNationality}
              placeholder="Select your nationality…"
              testId="hero-nationality-select"
            />
          </div>
        </div>

        {/* Step 2: Applying from */}
        <div className="flex flex-col">
          <label className="text-xs font-semibold text-[#0f1b2d]">Where are you applying from?</label>
          <div className="text-[11px] text-[#64748b] mt-0.5">The country where you will attend your visa appointment</div>
          <div className="mt-auto pt-1.5">
            <CountryCombobox
              options={WORLD_COUNTRIES}
              value={applyingFrom}
              onChange={setApplyingFrom}
              disabled={!nationality || blocked}
              placeholder="Select country…"
              testId="hero-applying-from-select"
            />
          </div>
        </div>

        {/* Step 3: Destination */}
        <div className="flex flex-col">
          <label className="text-xs font-semibold text-[#0f1b2d]">Destination country</label>
          <div className="mt-auto pt-1.5">
            <CountryCombobox
              options={SCHENGEN_COUNTRIES}
              value={country}
              onChange={setCountry}
              valueField="name"
              disabled={!applyingFrom || blocked}
              placeholder="Select destination country…"
              testId="hero-country-select"
            />
          </div>
        </div>
      </div>

      {/* Banner (eligible / schengen-warning / visa-free) */}
      <EligibilityBanner
        nationalityCode={nationality}
        applyingFromCode={applyingFrom}
        destinationName={country}
        visaFreeAcknowledged={visaFreeAck}
        onVisaFreeAcknowledge={() => setVisaFreeAck(true)}
        testId="hero-eligibility"
      />

      {country && !blocked && (
        <div className="mt-5">
          <label className="text-xs font-semibold text-[#0f1b2d]">Delivery</label>
          <div className="mt-1.5 grid grid-cols-2 gap-2" data-testid="hero-delivery-toggle">
            <button
              type="button"
              onClick={() => setDelivery("normal")}
              data-testid="hero-delivery-normal"
              className={`flex items-center justify-center gap-1.5 text-sm font-semibold py-2.5 rounded-md border-2 transition-all ${delivery === "normal" ? "border-[#2563eb] bg-[#eff6ff] text-[#2563eb]" : "border-[#e2e8f0] text-[#475569] hover:border-[#94a3b8]"}`}
            >
              <Clock className="w-3.5 h-3.5" /> 24h Delivery
            </button>
            <button
              type="button"
              onClick={() => setDelivery("express")}
              data-testid="hero-delivery-express"
              className={`flex items-center justify-center gap-1.5 text-sm font-semibold py-2.5 rounded-md border-2 transition-all ${delivery === "express" ? "border-[#2563eb] bg-[#eff6ff] text-[#2563eb]" : "border-[#e2e8f0] text-[#475569] hover:border-[#94a3b8]"}`}
            >
              <Zap className="w-3.5 h-3.5" /> Express 3h
            </button>
          </div>
        </div>
      )}

      {country && !blocked && (
        <div className="mt-5 space-y-2.5" data-testid="hero-packages">
          {loading && packages.length === 0 ? (
            <div className="space-y-2.5">
              {[1, 2, 3].map((i) => <div key={i} className="h-16 rounded-md bg-[#f1f5f9] animate-pulse" />)}
            </div>
          ) : (
            packages.map((p) => {
              const active = selectedPkg === p.id;
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setSelectedPkg(p.id)}
                  data-testid={`hero-pkg-${p.id}`}
                  className={`w-full text-left p-3 rounded-md border-2 transition-all relative ${active ? "border-[#2563eb] bg-[#eff6ff]" : "border-[#e2e8f0] hover:border-[#94a3b8]"}`}
                >
                  {p.most_popular && (
                    <div className="absolute -top-2 right-3 px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest bg-[#2563eb] text-white rounded">Most popular</div>
                  )}
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <div className="font-display font-bold text-base text-[#0f1b2d]">{p.name}</div>
                          <div className="text-[11px] text-[#64748b]">{labelForItems(p.items || [])}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-display font-black text-xl text-[#0f1b2d]">${Math.round(priceFor(p))}</div>
                      <div className="text-[10px] text-[#64748b]">{delivery === "express" ? "3h delivery" : "24h delivery"}</div>
                    </div>
                  </div>

                  {active && (
                    <div className="mt-3 pt-3 border-t border-[#bfdbfe]" data-testid={`hero-pkg-expanded-${p.id}`}>
                      <div className="text-[10px] uppercase tracking-wider text-[#64748b] mb-1.5">Preview</div>
                      <ScrollRow ariaLabel="Package preview">
                        {(p.items || []).map((it, i) => (
                          <PreviewCard key={i} item={it} size="thumb" />
                        ))}
                      </ScrollRow>
                    </div>
                  )}
                </button>
              );
            })
          )}
        </div>
      )}

      {country && !blocked && selectedPkg && (
        <div className="mt-5" data-testid="hero-addons-section">
          <label className="text-xs font-semibold text-[#0f1b2d]">Optional add-ons</label>
          <p className="text-[11px] text-[#64748b] mt-0.5">Each <span className="font-semibold text-[#0f1b2d]">+$19</span></p>
          <div className="mt-2 space-y-2">
            {ADD_ONS.map((a) => {
              const checked = selectedAddons.includes(a.id);
              const Icon = a.icon;
              return (
                <button
                  key={a.id}
                  type="button"
                  data-testid={`hero-addon-toggle-${a.id}`}
                  onClick={() => toggleAddon(a.id)}
                  className={`w-full text-left p-3 rounded-md border-2 transition-all relative ${checked ? "border-[#2563eb] bg-[#eff6ff]" : "border-[#e2e8f0] bg-white hover:border-[#94a3b8]"}`}
                >
                  <div className="flex items-center gap-2.5 pr-8">
                    <Icon className={`w-4 h-4 flex-shrink-0 ${checked ? "text-[#2563eb]" : "text-[#475569]"}`} />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-[#0f1b2d] text-sm">{a.name}</div>
                      <div className="text-[11px] text-[#64748b] truncate">{a.description}</div>
                    </div>
                    <div className="text-xs font-display font-bold text-[#2563eb] flex-shrink-0">+${a.price}</div>
                  </div>
                  <div className={`absolute top-3 right-3 w-4 h-4 rounded grid place-items-center transition ${checked ? "bg-[#2563eb] text-white" : "border-2 border-[#cbd5e1] bg-white"}`}>
                    {checked && <Check className="w-2.5 h-2.5" strokeWidth={3} />}
                  </div>
                </button>
              );
            })}
          </div>
          {selectedAddons.length > 0 && (
            <div className="mt-2 text-[11px] text-[#0f1b2d]" data-testid="hero-addons-summary">
              <span className="font-semibold text-[#2563eb]">{selectedAddons.length}</span> add-on{selectedAddons.length === 1 ? "" : "s"} · <span className="font-semibold">+${addOnTotal}</span>
            </div>
          )}
        </div>
      )}

      {country && !blocked && selectedPkg && (() => {
        const pkg = packages.find((p) => p.id === selectedPkg);
        if (!pkg) return null;
        const total = Math.round(priceFor(pkg)) + addOnTotal;
        return (
          <div className="mt-5 pt-4 border-t border-[#e2e8f0] flex items-center justify-between" data-testid="hero-total-row">
            <div className="text-xs uppercase tracking-widest text-[#64748b] font-semibold">Total</div>
            <div className="font-display font-black text-2xl text-[#0f1b2d]" data-testid="hero-total-amount">${total}</div>
          </div>
        );
      })()}

      <button
        onClick={start}
        disabled={!canStart}
        data-testid="hero-start-application"
        className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#2563eb] hover:bg-[#1d4ed8] disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-md transition-all"
      >
        Start your application <ArrowRight className="w-4 h-4" />
      </button>
      <p className="mt-2 text-[11px] text-center text-[#64748b]">Takes less than 10 minutes to complete</p>
    </div>
  );
}
